import React from 'react';
import { colors } from '@styleseat/brand';
import { CardElement } from '../StripeElement';
import './StripeCardInput.module.scss';
import { StripeCardChangeEvent, StripeElement } from '../types';
import { StripeElementProps } from '../StripeElementProps';

type StripeCardInputProps = {
  /** Called when the card number is completed. Receives a Stripe card instance. */
  onComplete?: (value: StripeElement, event: StripeCardChangeEvent) => void;
  /** Called when the card number is emptied. */
  onClear?: (value: StripeElement, event: StripeCardChangeEvent) => void;
  /**
   * Called when an error is reported by Stripe. Receives the error message
   * as the sole argument. May return an error message to use in the stead of
   * the one in the argument. */
  onError?: (message: string, event: StripeCardChangeEvent) => void;
  /**
   * Called when the card is currently being modified. Receives a Stripe
   * card instance. */
  onChange?: (value: StripeElement, event: StripeCardChangeEvent) => void;
  /**
   * Called when the card is currently being modified. Receives a Stripe
   * card instance. */
  onFocus?: StripeElementProps['onFocus'];
  /** True to focus on credit card input field */
  autoFocus?: boolean;
  /** list of styles passed onto the stripe element */
  styles?: Record<string, any>;
};

/**
 * Wraps the stripe card element with utility functions
 * to make it more consistent with how react handles events
 * @constructor
 */
export default function StripeCardInput({
  onComplete,
  onClear,
  onError,
  onChange,
  onFocus,
  autoFocus,
  styles,
}: StripeCardInputProps) {
  const handleCardChange = (event: StripeCardChangeEvent) => {
    // Stripe's internal types seem to be mismatched.
    // The event is a stripe ElementChangeResponse, but the event's value is a stripe Element.
    if (event.complete) {
      onComplete(event.value as unknown as StripeElement, event);
    } else {
      onChange(event.value as unknown as StripeElement, event);
    }

    if (event.error) {
      onError(event.error.message, event);
    }

    if (event.empty) {
      onClear(event.value as unknown as StripeElement, event);
    }
  };

  return (
    <CardElement
      styleName="card-input"
      testID="card-element"
      options={{
        style: styles,
      }}
      onChange={handleCardChange}
      onFocus={onFocus}
      autoFocus={autoFocus}
    />
  );
}

StripeCardInput.defaultProps = {
  onError: () => {},
  onComplete: () => {},
  onChange: () => {},
  onClear: () => {},
  onFocus: () => {},
  autoFocus: false,
  styles: {
    base: {
      fontSize: '15px',
      '::placeholder': {
        color: colors.silver,
      },
    },
    invalid: {
      color: '#a12e43',
      iconColor: '#a12e43',
    },
  },
};
